import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
  </div>
);

// Landing
const LandingPage = React.lazy(() => import("./screen/LandingPage/LandingPage"));
const PresensiDigital = React.lazy(() => import("./screen/LandingPage/PresensiDigital/PresensiDigital"));
const RiwayatPresensi = React.lazy(() => import("./screen/LandingPage/RiwayatPresensi/RiwayatPresensi"));
const LaporanKegiatanLandingPage = React.lazy(() => import("./screen/LandingPage/LaporanKegiatan/LaporanKegiatanLP"));
const PerformaKerja = React.lazy(() => import("./screen/LandingPage/PerformaKerja/PerformaKerja"));
const JadwalKerja = React.lazy(() => import("./screen/LandingPage/JadwalKerja/JadwalKerja"));
const PerizinanLP = React.lazy(() => import("./screen/LandingPage/Perizinan/PerizinanLP"));
const Pricing = React.lazy(() => import("./screen/LandingPage/Pricing/Pricing"));
const TeamStructure = React.lazy(() => import("./screen/LandingPage/Team Structure/TeamStructure"));
const SK = React.lazy(() => import("./screen/LandingPage/Syarat dan Ketentuan/SK"));
const PrivacyPolicy = React.lazy(() => import("./screen/LandingPage/Privacy Policy/PrivacyPolicy"));

// Auth
const RegisterKaryawanModal = React.lazy(() => import("./screen/Auth/register-karyawan/RegisterKaryawan"));
const RegisterKaryawanPage: React.FC = () => {
  const nav = useNavigate();
  return <Suspense fallback={<LoadingFallback />}><RegisterKaryawanModal visible={true} onClose={() => nav("/")} onSwitchToLogin={() => nav("/")} /></Suspense>;
};
const RegisterKaryawan = RegisterKaryawanPage;
const RegisterPerusahaan = React.lazy(() => import("./screen/Auth/register-perusahaan/RegisterPerusahaan"));
const DetailPembayaran = React.lazy(() => import("./screen/Auth/detail-pembayaran/DetailPembayaran"));
const PaymentMandiri = React.lazy(() => import("./screen/Auth/paymentMandiri/paymentMandiri"));
const PaymentBri = React.lazy(() => import("./screen/Auth/payment-Bri/paymentBri"));
const PaymentBni = React.lazy(() => import("./screen/Auth/payment-Bni/paymentBni"));
const PaymentAlfamart = React.lazy(() => import("./screen/Auth/payment-alfamart/paymentAlfamart"));
const PaymentIndomaret = React.lazy(() => import("./screen/Auth/payment-indomaret/paymentIndomaret"));
const PaymentGopay = React.lazy(() => import("./screen/Auth/payment-gopay/paymentGopay"));
const PaymentBca = React.lazy(() => import("./screen/Auth/payment-Bca/paymentBca"));
const PaymentPermata = React.lazy(() => import("./screen/Auth/payment-permata/paymentPermata"));

// Absensi (heavy — includes face-api.js)
const ClockIn = React.lazy(() => import("./screen/absensi/ClockIn/ClockIn/ClockIn"));
const ClockInUserLocation = React.lazy(() => import("./screen/absensi/ClockIn/user-location/UserLocation"));
const ClockOut = React.lazy(() => import("./screen/absensi/ClockOut/ClockOut"));
const Camera = React.lazy(() => import("./screen/absensi/camera/camera"));
const KonfirmasiClockIn = React.lazy(() => import("./screen/absensi/KonfirmasiClockIn/KonfirmasiClockIn"));
const KonfirmasiClockOut = React.lazy(() => import("./screen/absensi/KonfirmasiClockOut/KonfirmasiClockOut"));
const DetailClockIn = React.lazy(() => import("./screen/absensi/DetailClockIn/DetailClockIn"));
const DetailClockOut = React.lazy(() => import("./screen/absensi/DetailClockIOut/DetailClockOut"));
const ManualAbsensi = React.lazy(() => import("./screen/absensi/ManualAbsensi/ManualAbsensi"));

// Laporan Kegiatan
const LaporanKegiatan = React.lazy(() => import("./screen/LaporanKegiatan/laporan-kegiatan/LaporanKegiatan"));
const DetailLaporanKegiatan = React.lazy(() => import("./screen/LaporanKegiatan/Detail-Laporan-Kegiatan/DetailLaporan"));
const EditLaporanKegiatan = React.lazy(() => import("./screen/LaporanKegiatan/EditLaporanKegiatan/EditLaporanKegiatan"));

// Perizinan
const Perizinan = React.lazy(() => import("./screen/Perizinan/Perizinan"));
const PengajuanIzin = React.lazy(() => import("./screen/Perizinan/PengajuanIzin/PengajuanIzin"));
const PenerimaanIzin = React.lazy(() => import("./screen/Perizinan/PenerimaanIzin/PenerimaanIzin"));
const PenolakanIzin = React.lazy(() => import("./screen/Perizinan/PenolakanIzin/PenolakanIzin"));
const FormPerizinan = React.lazy(() => import("./screen/Perizinan/FormPerizinan/FormPerizinan"));
const DetailPerizinan = React.lazy(() => import("./screen/Perizinan/DetailPerizinan/DetailPerizinan"));

// Profile
const Profile = React.lazy(() => import("./screen/profile/profile/Profile"));
const Informasi = React.lazy(() => import("./screen/profile/Informasi/Informasi"));
const KataSandi = React.lazy(() => import("./screen/profile/kata-sandi/KataSandi"));
const Gaji = React.lazy(() => import("./screen/profile/gaji/gaji"));
const Perusahaan = React.lazy(() => import("./screen/profile/perusahaan/perusahaan"));

// User & Admin
const User = React.lazy(() => import("./screen/user/user"));
const DashboardAdmin = React.lazy(() => import("./screen/Admin/DashboardAdmin/DashboardAdmin"));
const MonitoringKehadiran = React.lazy(() => import("./screen/Admin/Monitoring/MonitoringKehadiran"));
const AdminPerizinan = React.lazy(() => import("./screen/Admin/Perizinan/Perizinan/Perizinan"));
const Testing = React.lazy(() => import("./screen/Admin/testing/testing"));
const PerizinanAll = React.lazy(() => import("./screen/Admin/Perizinan/list-perizinan/list-perizinan-all/PerizinanAll"));
const PerizinanAccept = React.lazy(() => import("./screen/Admin/Perizinan/list-perizinan/list-perizinan-accept/PerizinanAccept"));
const PerizinanReject = React.lazy(() => import("./screen/Admin/Perizinan/list-perizinan/list-perizinan-reject/PerizinanReject"));

const Router: React.FC = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route>
            <Route path="/" element={<LandingPage />}>
              <Route path="/presensi-digital" element={<PresensiDigital />} />
              <Route path="/riwayat-presensi" element={<RiwayatPresensi />} />
              <Route path="/laporan-kegiatan" element={<LaporanKegiatanLandingPage />} />
              <Route path="/performa-kerja" element={<PerformaKerja />} />
              <Route path="/jadwal-kerja" element={<JadwalKerja />} />
              <Route path="/perizinanLP" element={<PerizinanLP />} />
            </Route>
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/team-structure" element={<TeamStructure />} />
            <Route path="/syarat-ketentuan" element={<SK />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            <Route path="/perizinan" element={<Perizinan />}>
              <Route path="/perizinan" element={<PengajuanIzin />} />
              <Route path="penerimaan-izin" element={<PenerimaanIzin />} />
              <Route path="penolakan-izin" element={<PenolakanIzin />} />
            </Route>
            <Route path="/form-perizinan" element={<FormPerizinan />} />
            <Route path="/detail-perizinan/:id" element={<DetailPerizinan />} />
            <Route path="/laporan-kegiatan/:id" element={<LaporanKegiatan />} />

            <Route path="/absensi" element={<ClockIn />} />
            <Route path="/absensi/clock-in/user-location" element={<ClockInUserLocation />} />
            <Route path="/absensi/clock-in/user-location/camera" element={<Camera />} />
            <Route path="/absensi/clock-out" element={<ClockOut />} />
            <Route path="/absensi/manual-absensi" element={<ManualAbsensi />} />
            <Route
              path="/absensi/clock-in/user-location/camera/konfirmasi-clock-in"
              element={<KonfirmasiClockIn />}
            />
            <Route
              path="/absensi/clock-out/user-location/camera/konfirmasi-clock-out"
              element={<KonfirmasiClockOut />}
            />
            <Route
              path="/absensi/clock-in/user-location/camera/konfirmasi-clock-in/detail-clock-in"
              element={<DetailClockIn />}
            />
            <Route
              path="/absensi/clock-out/user-location/camera/konfirmasi-clock-out/detail-clock-out"
              element={<DetailClockOut />}
            />

            <Route path="/user" element={<User />} />
            <Route path="/laporan-kegiatan/detail-laporan-kegiatan" element={<DetailLaporanKegiatan />} />
            <Route path="/laporan-kegiatan/edit-laporan-kegiatan/:id" element={<EditLaporanKegiatan />} />

            <Route path="/profile" element={<Profile />}>
              <Route path="/profile/informasi" element={<Informasi />} />
              <Route path="/profile/kata-sandi" element={<KataSandi />} />
              <Route path="/profile/gaji" element={<Gaji />} />
              <Route path="/profile/profile-perusahaan" element={<Perusahaan />} />
            </Route>

            <Route path="/auth/register-karyawan" element={<RegisterKaryawan />} />
            <Route path="/auth/register-perusahaan" element={<RegisterPerusahaan />} />
            <Route path="/auth/register-perusahaan/detail-pembayaran" element={<DetailPembayaran />} />
            <Route path="/auth/register-perusahaan/detail-pembayaran/mandiri_va" element={<PaymentMandiri />} />
            <Route path="/auth/register-perusahaan/detail-pembayaran/bri_va" element={<PaymentBri />} />
            <Route path="/auth/register-perusahaan/detail-pembayaran/bni_va" element={<PaymentBni />} />
            <Route path="/auth/register-perusahaan/detail-pembayaran/alfamart" element={<PaymentAlfamart />} />
            <Route path="/auth/register-perusahaan/detail-pembayaran/indomaret" element={<PaymentIndomaret />} />
            <Route path="/auth/register-perusahaan/detail-pembayaran/gopay_online" element={<PaymentGopay />} />
            <Route path="/auth/register-perusahaan/detail-pembayaran/bca_va" element={<PaymentBca />} />
            <Route path="/auth/register-perusahaan/detail-pembayaran/permata_va" element={<PaymentPermata />} />
          </Route>

          <Route>
            <Route path="/admin" element={<DashboardAdmin />} />
            <Route path="/admin/monitoring-kehadiran" element={<MonitoringKehadiran />} />
            <Route path="/testing" element={<AdminPerizinan />} />
            <Route path="/admin/perizinan" element={<Testing />}>
              <Route path="/admin/perizinan/menunggu-persetujuan" element={<PerizinanAll />} />
              <Route path="/admin/perizinan/diterima" element={<PerizinanAccept />} />
              <Route path="/admin/perizinan/ditolak" element={<PerizinanReject />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default Router;
